import React, { useMemo } from 'react';
import { css } from '@emotion/react';
import { genAttributes, STEPS, useCrossdockingSteps } from '@onward-delivery/core';
import { useClientUser } from '@/hooks';
import { Grid, Tooltip } from '@material-ui/core';
import { useSplitButtonState } from '@/components/SplitOrderModal/hooks';
import { isSplitChild } from '../utilities/splitOrders';
import Claimed from './Claimed';
import Assigned from './Assigned';
import Received from './Received';
import PickedUp from './PickedUp';
import Complete from './Complete';
import Crossdocked from './Crossdocked';
import CrossdockPickup from './CrossdockPickup';
import Attempt from './Attempt';
import { Header2 } from './blocks';
import { OnwardCard } from '../Card';
import CustomerConfirm from './CustomerConfirm';
import Scheduled from './Scheduled';
import zipcode_to_timezone from 'zipcode-to-timezone';
import { PrimaryButton, SecondaryButton } from '@/styles/blocks';
import ReturnToSender from './ReturnToSender';
import { Label } from '../blocks';
import { Anchor, Header1 } from './blocks';
import MiddleMileSteps from './MiddleMileSteps';

export default function CrossDockingCard({
    order,
    isLoading,
    events,
    warehouse,
    callbacks,
    readOnly,
    crossdockDriver,
    finalMileDriver,
}) {
    const { user_id, isOnwardAdmin } = useClientUser();

    let steps = useCrossdockingSteps(events, order.order_type);
    if (user_id === order.middle_mile_carrier_id) {
        steps = steps.filter(([step, _]) => [STEPS.CLAIMED, STEPS.MIDDLE_MILE].includes(step));
    }

    const tz = useMemo(() => {
        const { zip } = genAttributes(order);
        return (
            zipcode_to_timezone.lookup(order[zip]) ||
            Intl.DateTimeFormat().resolvedOptions().timeZone ||
            'America/New_York'
        );
    }, [order]);

    const params = {
        order,
        events,
        isLoading,
        warehouse,
        callbacks,
        isDesktop: true,
        isReceived: events.some((e) => e.action === 'START:AWAITING'),
        isCrossdocked: events.some((e) => e.action.endsWith(':ADD_CD')),
        lastEvent: events[events.length - 1],
        tz,
        readOnly,
        finalMileDriver,
        crossdockDriver,
    };

    // Split Order shows only on the editable card, for an order that is neither a split parent nor a split child.
    const canSplit =
        !readOnly &&
        typeof callbacks?.splitOrder === 'function' &&
        order.order_status !== 'split' &&
        !isSplitChild(order);

    const split = useSplitButtonState({ orderId: order.order_id, skip: !canSplit });
    const splitDisabled = isLoading || split.loading || split.reasons.length > 0;
    const splitTooltip = split.reasons.length > 0 && !split.loading ? split.reasons : null;

    const content = steps.map(([step, meta]) => {
        switch (step) {
            case STEPS.MIDDLE_MILE:
                return (
                    <MiddleMileSteps
                        key={step}
                        {...meta}
                        {...params}
                        readOnly={
                            readOnly ||
                            !(user_id === order.middle_mile_carrier_id || isOnwardAdmin || user_id === order.shipper_id)
                        }
                    />
                );
            case STEPS.CLAIMED:
                return <Claimed key={step} {...meta} {...params} />;
            case STEPS.SCHEDULED:
                return (
                    <React.Fragment key={step}>
                        <Scheduled {...meta} {...params} />
                        <CustomerConfirm {...meta} {...params} />
                        <Assigned {...meta} {...params} />
                    </React.Fragment>
                );
            case STEPS.RECEIVED:
                return <Received key={step} {...meta} {...params} />;
            case STEPS.PICKED_UP:
                return <PickedUp key={step} {...meta} {...params} />;
            case STEPS.COMPLETE:
                return <Complete key={step} {...meta} {...params} />;
            case STEPS.CROSS_DOCKED:
                return <Crossdocked key={step} {...meta} {...params} />;
            case STEPS.CROSS_DOCK_PICKUP:
                return <CrossdockPickup key={step} {...meta} {...params} />;
            case STEPS.ATTEMPT:
                return <Attempt key={step} {...meta} {...params} />;
            case STEPS.RETURN_TO_SENDER:
                return <ReturnToSender key={step} {...meta} {...params} />;
        }
    });

    return (
        <OnwardCard>
            <Grid direction="column" container>
                <Grid
                    direction="row"
                    container
                    justifyContent="space-between"
                    item
                    css={css`
                        margin-bottom: 32px;
                    `}
                >
                    <Header2>LTL Delivery Checklist - {order?.order_number}</Header2>
                    <Grid
                        item
                        container
                        direction="row"
                        alignItems="flex-start"
                        wrap="nowrap"
                        css={css`
                            width: auto;
                            gap: 16px;
                        `}
                    >
                        <Grid
                            item
                            container
                            direction="column"
                            css={css`
                                width: auto;
                            `}
                        >
                            {crossdockDriver && (
                                <Label>
                                    Crossdock Driver: {crossdockDriver.username}
                                    {events &&
                                        events.length > 0 &&
                                        callbacks?.sendWeblink &&
                                        (() => {
                                            const crossdockEvent =
                                                [...events]
                                                    .reverse()
                                                    .find(
                                                        (e) => (e.action || '').includes('ADD_CD') && !!e.wh_driver
                                                    ) || null;

                                            return crossdockEvent ? (
                                                <Anchor
                                                    css={css`
                                                        margin-left: 8px;
                                                    `}
                                                    onClick={() => callbacks.sendWeblink(crossdockEvent, 'CROSSDOCK')}
                                                >
                                                    <Header1>Text Driver</Header1>
                                                </Anchor>
                                            ) : null;
                                        })()}
                                </Label>
                            )}
                            {finalMileDriver && <Label>Final Mile Driver: {finalMileDriver.username}</Label>}
                        </Grid>
                        {canSplit && !split.hidden && (
                            <Tooltip
                                placement="bottom-end"
                                title={
                                    splitTooltip
                                        ? splitTooltip.map((reason) => <div key={reason}>{reason}</div>)
                                        : ''
                                }
                            >
                                {/* A disabled button fires no pointer events, so the span carries the tooltip. */}
                                <span tabIndex={0}>
                                    <SecondaryButton
                                        disabled={splitDisabled}
                                        onClick={() => callbacks.splitOrder()}
                                        css={css`
                                            width: auto;
                                            white-space: nowrap;
                                        `}
                                    >
                                        Split Order
                                    </SecondaryButton>
                                </span>
                            </Tooltip>
                        )}
                    </Grid>
                </Grid>
                {content}
                <Grid
                    direction="row"
                    container
                    css={css`
                        justify-content: flex-start;
                    `}
                >
                    <PrimaryButton
                        orange={true}
                        disabled={isLoading}
                        onClick={() =>
                            callbacks.createException({
                                type: 'DROPOFF',
                            })
                        }
                        css={css`
                            width: auto;
                        `}
                    >
                        Add exception
                    </PrimaryButton>
                </Grid>
            </Grid>
        </OnwardCard>
    );
}
